import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Plus, X, Check, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";

const CUSTOM_KEY_PREFIX = "custom_";

/**
 * Dialog to add/remove default & suggested wheel areas and create custom ones.
 *
 * Props:
 * - open, onOpenChange
 * - defaultAreas: master default list
 * - suggestedAreas: master suggestion list
 * - customAreas: user's existing custom areas (pulled from their saved wheel)
 * - activeKeys: Set<string> of currently-active keys
 * - maxAreas: cap (default 12)
 * - onApply({ activeKeys: Set, newCustoms: [{key,label,color,description}] })
 */
const CustomizeAreasDialog = ({
  open,
  onOpenChange,
  defaultAreas = [],
  suggestedAreas = [],
  customAreas = [],
  activeKeys = new Set(),
  maxAreas = 12,
  onApply,
}) => {
  const [draftKeys, setDraftKeys] = useState(new Set(activeKeys));
  const [draftCustoms, setDraftCustoms] = useState(customAreas); // [{key,label,color}]
  const [newLabel, setNewLabel] = useState("");

  // Reset drafts whenever dialog opens
  useEffect(() => {
    if (open) {
      setDraftKeys(new Set(activeKeys));
      setDraftCustoms(customAreas);
      setNewLabel("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const totalActive = draftKeys.size;
  const atCap = totalActive >= maxAreas;

  const toggle = (key, isAdding) => {
    const next = new Set(draftKeys);
    if (isAdding) {
      if (atCap) {
        toast.error(`You can have at most ${maxAreas} areas. Remove one first.`);
        return;
      }
      next.add(key);
    } else {
      next.delete(key);
    }
    setDraftKeys(next);
  };

  const addCustom = () => {
    const label = newLabel.trim();
    if (!label) return;
    if (label.length > 40) {
      toast.error("Keep the name under 40 characters");
      return;
    }
    if (atCap) {
      toast.error(`You can have at most ${maxAreas} areas. Remove one first.`);
      return;
    }
    // Temp client key — backend will assign final slug on save
    const tmpKey = `${CUSTOM_KEY_PREFIX}new_${Math.random().toString(36).slice(2, 8)}`;
    const palette = ["#7c3aed", "#dc2626", "#0891b2", "#059669", "#c026d3", "#2563eb", "#ea580c", "#16a34a", "#db2777", "#475569"];
    const usedColors = new Set(draftCustoms.map((c) => c.color));
    const color = palette.find((c) => !usedColors.has(c)) || palette[draftCustoms.length % palette.length];
    const newArea = { key: tmpKey, label, color, description: "", is_new: true };
    setDraftCustoms([...draftCustoms, newArea]);
    setDraftKeys(new Set([...draftKeys, tmpKey]));
    setNewLabel("");
  };

  const removeCustom = (key) => {
    setDraftCustoms(draftCustoms.filter((c) => c.key !== key));
    const next = new Set(draftKeys);
    next.delete(key);
    setDraftKeys(next);
  };

  const handleApply = () => {
    onApply?.({
      activeKeys: draftKeys,
      customs: draftCustoms,
    });
    onOpenChange(false);
  };

  const renderAreaRow = (area, { isCustom = false } = {}) => {
    const isActive = draftKeys.has(area.key);
    return (
      <div
        key={area.key}
        role="button"
        tabIndex={0}
        onClick={() => toggle(area.key, !isActive)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggle(area.key, !isActive);
          }
        }}
        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-all text-left cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-violet-400 ${
          isActive
            ? "bg-white border-violet-300 shadow-sm"
            : "bg-slate-50 border-slate-200 hover:border-slate-300"
        }`}
        data-testid={`customize-area-${area.key}`}
      >
        <span
          className="flex-shrink-0 w-3 h-3 rounded-full"
          style={{ backgroundColor: area.color }}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-slate-900 truncate">
              {area.label}
            </span>
            {isCustom && (
              <span className="text-[10px] font-semibold text-violet-600 uppercase tracking-wide">
                Custom
              </span>
            )}
          </div>
          {area.description && (
            <p className="text-[11px] text-slate-500 leading-snug mt-0.5 line-clamp-1">
              {area.description}
            </p>
          )}
        </div>
        {isCustom && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); removeCustom(area.key); }}
            className="flex-shrink-0 p-1 rounded-full text-slate-400 hover:text-red-500 hover:bg-red-50"
            data-testid={`customize-remove-custom-${area.key}`}
            title="Delete this custom area"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
        <span
          className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center ${
            isActive ? "bg-violet-600 text-white" : "bg-slate-200 text-slate-400"
          }`}
        >
          {isActive ? <Check className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
        </span>
      </div>
    );
  };

  const addedSuggested = suggestedAreas.filter((s) => draftKeys.has(s.key));
  const availableSuggested = suggestedAreas.filter((s) => !draftKeys.has(s.key));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg max-h-[85vh] overflow-y-auto"
        data-testid="customize-areas-dialog"
      >
        <DialogHeader>
          <DialogTitle
            className="flex items-center gap-2"
            style={{ fontFamily: "system-ui, sans-serif" }}
          >
            <Sparkles className="w-5 h-5 text-violet-600" />
            Customize your wheel
          </DialogTitle>
          <DialogDescription>
            Tailor the Standard Wheel to match your life.{" "}
            <span className="font-semibold text-slate-700">
              {totalActive}/{maxAreas}
            </span>{" "}
            areas active.
          </DialogDescription>
        </DialogHeader>

        {/* Default — always shown, toggleable */}
        <section className="mt-2">
          <h3 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-2">
            Standard Wheel
          </h3>
          <div className="space-y-1.5">
            {defaultAreas.map((a) => renderAreaRow(a))}
          </div>
        </section>

        {/* Custom areas (existing + newly drafted) */}
        {draftCustoms.length > 0 && (
          <section className="mt-5">
            <h3 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-2">
              Your custom areas
            </h3>
            <div className="space-y-1.5">
              {draftCustoms.map((a) => renderAreaRow(a, { isCustom: true }))}
            </div>
          </section>
        )}

        {/* Suggested — included (if any active) */}
        {addedSuggested.length > 0 && (
          <section className="mt-5">
            <h3 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-2">
              Suggested — added
            </h3>
            <div className="space-y-1.5">
              {addedSuggested.map((a) => renderAreaRow(a))}
            </div>
          </section>
        )}

        {/* Suggested — available */}
        {availableSuggested.length > 0 && (
          <section className="mt-5">
            <h3 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-2">
              Suggested areas
            </h3>
            <div className="space-y-1.5">
              {availableSuggested.map((a) => renderAreaRow(a))}
            </div>
          </section>
        )}

        {/* Create your own */}
        <section className="mt-5">
          <h3 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-2">
            Create your own
          </h3>
          <div className="flex items-center gap-2">
            <Input
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustom(); } }}
              placeholder="e.g. Meditation, Travel..."
              maxLength={40}
              className="text-sm"
              data-testid="customize-new-area-input"
            />
            <Button
              type="button"
              onClick={addCustom}
              disabled={!newLabel.trim() || atCap}
              className="rounded-full bg-violet-600 hover:bg-violet-700 text-white flex-shrink-0"
              data-testid="customize-add-custom-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add
            </Button>
          </div>
          <p className="text-[11px] text-slate-400 mt-1.5">
            Up to {maxAreas} areas total · {maxAreas - totalActive} slot{maxAreas - totalActive === 1 ? "" : "s"} left
          </p>
        </section>

        {/* Actions */}
        <div className="flex items-center justify-end gap-2 mt-6 pt-4 border-t border-slate-100">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="rounded-full"
            data-testid="customize-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleApply}
            disabled={totalActive === 0}
            className="bg-violet-600 hover:bg-violet-700 text-white rounded-full"
            data-testid="customize-apply"
          >
            Apply changes
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CustomizeAreasDialog;
